import { Button, Form, Modal, Switch, Table, Tag, Typography } from "antd";
import React, { useEffect, useState } from "react";

import { formatDateTime } from "../../helpers/date";

import BonusCampaignForm from "./BonusCampaignForm";
import UserStatusTag from "../shared/UserStatusTag";
import { buildPackageAmounts } from "./packageAmounts";

const { Text } = Typography;

const BonusCampaignList = ({
  campaigns,
  loading,
  canUpdate,
  onToggle,
  onUpdate,
  welcomePackageLabels = {},
}) => {
  const [data, setData] = useState(campaigns || []);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [editPackageAmounts, setEditPackageAmounts] = useState(
    buildPackageAmounts()
  );
  const [editForm] = Form.useForm();

  useEffect(() => {
    setData(campaigns || []);
  }, [campaigns]);

  const openEditModal = (record) => {
    editForm.resetFields();

    setSelectedCampaign(record);

    const campaignDetails = record.campaignDetails || [];

    const packageAmounts = buildPackageAmounts(
      campaignDetails.map((detail) => detail.packageAmount),
      Object.keys(welcomePackageLabels)
    );

    const campaignDetailsValues = packageAmounts.map((amount) => {
      const match = campaignDetails.find((d) => d.packageAmount === amount);
      return {
        packageAmount: amount,
        bonus: match?.bonus ?? 0,
        limit: match?.limit,
      };
    });

    setEditPackageAmounts(packageAmounts);

    editForm.setFieldsValue({
      name: record.name,
      description: record.description,
      isFirstTopupOnly: record.isFirstTopupOnly || false,
      badge_en: record.badge?.en || "",
      badge_zh: record.badge?.zh || "",
      badge_ms: record.badge?.ms || "",
      campaignDetails: campaignDetailsValues || [],
      category: record.category,
      status: record?.status || "-",
    });

    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    console.log("handleEditSubmit", values);
    await onUpdate(selectedCampaign._id, values);
    setEditModalVisible(false);
    setSelectedCampaign(null);
    editForm.resetFields();
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setSelectedCampaign(null);
    editForm.resetFields();
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "_id",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Target",
      dataIndex: "category",
      render: (cat) =>
        cat === "userStatus" ? (
          <Tag color="blue">By User Status</Tag>
        ) : (
          <Tag color="green">All Users</Tag>
        ),
    },
    {
      title: "Selected User Status",
      dataIndex: "status",
      render: (text) =>
        text ? <UserStatusTag statusKey={text}>{text}</UserStatusTag> : "-",
    },
    {
      title: "Period",
      render: (_, record) => (
        <span>
          {record.startDate
            ? formatDateTime(record.startDate).substring(0, 10)
            : "-"}{" "}
          →{" "}
          {record.endDate
            ? formatDateTime(record.endDate).substring(0, 10)
            : "-"}
        </span>
      ),
    },
    {
      title: "For First Topup",
      dataIndex: "isFirstTopupOnly",
      render: (text) =>
        text === true ? (
          <Tag color="green">Enable</Tag>
        ) : (
          <Tag color="default">Disable</Tag>
        )
    },
    {
      title: "Active",
      dataIndex: "isActive",
      render: (value, record) =>
        canUpdate ? (
          <Switch
            checked={value}
            onChange={(checked) => onToggle(record._id, checked)}
          />
        ) : value ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
    },
    ...(canUpdate
      ? [
        {
          title: "Actions",
          fixed: "right",
          width: 100,
          render: (_, record) => (
            <Button type="default" onClick={() => openEditModal(record)}>
              Edit
            </Button>
          ),
        },
      ]
      : []),
  ];

  return (
    <>
      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={data}
        loading={false}
        pagination={false}
        scroll={{ x: true }}
      />

      <Modal
        title="Edit Details"
        visible={editModalVisible}
        onCancel={handleEditCancel}
        footer={null} // ✅ let form control submit
        destroyOnClose
        width="60%"
      >
        <div>
          <BonusCampaignForm
            form={editForm}
            loading={loading.create}
            mode="update"
            onSubmit={handleEditSubmit}
            onCancel={handleEditCancel}
            submitText="Update Campaign"
            selectedCampaign={selectedCampaign}
            welcomePackageLabels={welcomePackageLabels}
            packageAmounts={editPackageAmounts}
          />
        </div>
      </Modal>
    </>
  );
};

export default BonusCampaignList;
